/**
 * @fileoverview Job CRM Contacts — main view.
 *
 * Datatable that aggregates and dedupes every contact referenced on the
 * job across the eight summarization-agent-populated tables. Sits between
 * Searches and Day One in the job sidebar.
 *
 * V2:
 * - Reads from `api.contacts.queries.listJobContacts` (writable
 *   `jobContacts` overlay table).
 * - Per-contact email, phone, notes are editable from the detail panel
 *   with autosave; firm-level fallback rendered as "from firm" pills.
 * - Manual merge / unmerge and link-to-firm available from the detail
 *   panel.
 * - Reconcile is triggered on mount with a server-side debounce so
 *   refreshes stay cheap.
 *
 * The reconcile pipeline is the primary source of contacts, but job writers
 * can also add one manually via the "Add Contact" button (`NewJobContactDialog`
 * → `createJobContact`). Manual rows carry no source links and survive future
 * reconciles.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { isContactRole } from "@/lib/contact-roles";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Search,
  X,
  AlertCircle,
  RefreshCw,
  Download,
  Loader2,
  ChevronDown,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/ui/primitives/input";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { FacetedFilter } from "@/ui/composed/filters/faceted-filter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  ROLE_META,
  ROLE_ORDER,
  customRoleKey,
  distinctRoles,
  type ContactRole,
  type ContactType,
  type CustomJobRole,
  type JobContactRow,
} from "./types";
import { ContactsDataTable, type ContactsGroupView } from "./ContactsDataTable";
import { ContactDetailPanel } from "./ContactDetailPanel";
import { NewJobContactDialog } from "./NewJobContactDialog";
import {
  buildContactsCustomExportDatasets,
  exportContactsIpsToCsv,
  exportContactsTableToCsv,
  type ContactsCsvScope,
} from "./exportContacts";
import { CustomExportDialog } from "@/components/customExport";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";

const TYPE_FILTER_OPTIONS = CONTACT_TYPE_ORDER.map((t) => ({
  value: t,
  label: CONTACT_TYPE_META[t].label,
}));

interface ContactsViewProps {
  jobId: Id<"jobs">;
}

export function ContactsView({ jobId }: ContactsViewProps) {
  const [search, setSearch] = useState("");
  const [activeRoles, setActiveRoles] = useState<Set<ContactRole>>(new Set());
  const [activeCustomRoleIds, setActiveCustomRoleIds] = useState<
    Set<Id<"jobContactRoles">>
  >(new Set());
  const [activeTypes, setActiveTypes] = useState<Set<ContactType>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  // Corporate-group presentation (MOR-708). Grouped-expanded by default;
  // "collapsed" shows only top-level entries (ungrouped contacts + group
  // headers); "flat" is the pre-groups list.
  const [groupView, setGroupView] = useState<ContactsGroupView>("expanded");
  const [selectedContactId, setSelectedContactId] =
    useState<Id<"jobContacts"> | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<Id<"jobContacts">>>(
    () => new Set(),
  );

  const { data: contacts, isLoading } = useQuery({
    ...convexQuery(api.contacts.queries.listJobContacts, {
      jobId,
      showArchived,
    }),
  });

  const { data: sourceRowsByContact } = useQuery({
    ...convexQuery(api.contacts.queries.getJobContactSourceRowsForJob, {
      jobId,
    }),
  });

  // Job role drives write affordances — viewers can browse but not add / edit
  // (the backend also enforces this via `verifyJobWriteAccess`).
  const { data: job } = useQuery({
    ...convexQuery(api.jobs.queries.getJob, { jobId }),
  });
  const canEdit = job ? job.userRole !== "viewer" : false;

  // Org-defined custom roles (always manual; independent of reconcile). Used
  // to render custom role badges in the table and offer them in the filter.
  const { data: customRoles } = useQuery({
    ...convexQueryOrIdle(
      api.contacts.customRoles.listJobContactRoles,
      job ? { orgId: job.orgId } : undefined,
    ),
    enabled: Boolean(job),
  });
  const customRolesById = useMemo(() => {
    const map = new Map<string, CustomJobRole>();
    for (const role of customRoles ?? []) map.set(role._id, role);
    return map;
  }, [customRoles]);
  const customIdByKey = useMemo(() => {
    const map = new Map<string, Id<"jobContactRoles">>();
    for (const role of customRoles ?? [])
      map.set(customRoleKey(role._id), role._id);
    return map;
  }, [customRoles]);

  // Wrap the reconcile mutation in react-query's `useMutation` so we get an
  // `isPending` flag to feed the "Refreshing…" UI. `useConvexMutation` on its
  // own returns just the mutate function without a pending signal.
  const { mutateAsync: triggerReconcile, isPending: isManualReconciling } =
    useMutation({
      mutationFn: useConvexMutation(
        api.contacts.mutations.triggerJobContactReconcile,
      ),
    });

  // Bulk delete (MOR-930): confirmation dialog + pending state for the
  // selection toolbar's destructive action.
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const { mutateAsync: bulkDeleteContacts, isPending: isBulkDeleting } =
    useMutation({
      mutationFn: useConvexMutation(
        api.contacts.mutations.bulkDeleteJobContacts,
      ),
    });

  // The mount-time reconcile fires outside of user-triggered mutation state,
  // so track it separately. Cleared once the first mount-time call resolves
  // (success OR skipped-because-fresh both count as "not working anymore").
  const [isMountReconciling, setIsMountReconciling] = useState(true);

  // Combined signal driving every "we're working" indicator in this view.
  const isReconciling = isManualReconciling || isMountReconciling;

  // Mount-time reconcile (debounced server-side via `lastReconciledAt` so
  // a quick refresh / SPA back-nav doesn't fan out into duplicate runs).
  useEffect(() => {
    setIsMountReconciling(true);
    triggerReconcile({ jobId })
      .catch((err: Error) => {
        console.warn("[contacts] mount-time reconcile failed", err);
      })
      .finally(() => {
        setIsMountReconciling(false);
      });
    // Only fire once per mount per jobId. `triggerReconcile` is a stable
    // react-query mutate reference so re-including it would still be safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const allRows = useMemo<Array<JobContactRow>>(
    () => contacts ?? [],
    [contacts],
  );

  // Custom export tool (MOR-1048): compose / save / export over all rows.
  const [customExportOpen, setCustomExportOpen] = useState(false);
  const customExportDatasets = useMemo(
    () =>
      buildContactsCustomExportDatasets(allRows, sourceRowsByContact ?? null),
    [allRows, sourceRowsByContact],
  );

  // -------------------------------------------------------------------------
  // Derive filtered row set from search + active kinds.
  // -------------------------------------------------------------------------
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter((row) => {
      if (term) {
        const haystack = [
          row.displayName,
          row.abn ?? "",
          row.acn ?? "",
          row.effectiveEmail ?? "",
          row.effectivePhone ?? "",
          row.firmContact?.displayName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (activeRoles.size > 0 || activeCustomRoleIds.size > 0) {
        const builtInHit = distinctRoles(row).some((r) => activeRoles.has(r));
        const customHit = (row.customRoleIds ?? []).some((id) =>
          activeCustomRoleIds.has(id),
        );
        if (!builtInHit && !customHit) return false;
      }
      if (activeTypes.size > 0 && !activeTypes.has(row.contactType)) {
        return false;
      }
      return true;
    });
  }, [allRows, search, activeRoles, activeCustomRoleIds, activeTypes]);

  // Counts keyed by both built-in role enum values and custom-role keys
  // (`custom-role:<id>`), so a single "Role" filter can offer both.
  const roleCountsMap = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of allRows) {
      for (const role of distinctRoles(row)) {
        counts.set(role, (counts.get(role) ?? 0) + 1);
      }
      for (const id of row.customRoleIds ?? []) {
        const key = customRoleKey(id);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return counts;
  }, [allRows]);

  // Only offer roles that actually appear on contacts (plus any still-
  // selected roles so a filter chip doesn't vanish mid-click).
  const roleFilterOptions = useMemo(() => {
    const builtIn: Array<{ value: string; label: string }> = ROLE_ORDER.filter(
      (role) => (roleCountsMap.get(role) ?? 0) > 0 || activeRoles.has(role),
    ).map((role) => ({ value: role, label: ROLE_META[role].label }));
    const custom = (customRoles ?? [])
      .filter(
        (role) =>
          (roleCountsMap.get(customRoleKey(role._id)) ?? 0) > 0 ||
          activeCustomRoleIds.has(role._id),
      )
      .map((role) => ({ value: customRoleKey(role._id), label: role.name }));
    return [...builtIn, ...custom];
  }, [roleCountsMap, activeRoles, customRoles, activeCustomRoleIds]);

  const selectedRoleValues = useMemo(
    () => [
      ...Array.from(activeRoles),
      ...Array.from(activeCustomRoleIds).map(customRoleKey),
    ],
    [activeRoles, activeCustomRoleIds],
  );

  const typeCountsMap = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of allRows) {
      counts.set(row.contactType, (counts.get(row.contactType) ?? 0) + 1);
    }
    return counts;
  }, [allRows]);

  const lowConfidenceCount = allRows.filter(
    (c) => c.mergeConfidence === "low" && c.status === "active",
  ).length;
  const archivedCount = allRows.filter((c) => c.status === "archived").length;

  const isFiltered =
    activeRoles.size > 0 ||
    activeCustomRoleIds.size > 0 ||
    activeTypes.size > 0 ||
    search.trim().length > 0;

  const resetFilters = () => {
    setSearch("");
    setActiveRoles(new Set());
    setActiveCustomRoleIds(new Set());
    setActiveTypes(new Set());
  };

  // Drop selections that fell out of the filtered/visible set.
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visible = new Set(filteredRows.map((r) => r._id));
    const next = new Set([...selectedIds].filter((id) => visible.has(id)));
    if (next.size !== selectedIds.size) setSelectedIds(next);
  }, [filteredRows, selectedIds]);

  const toggleSelected = (id: Id<"jobContacts">) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = (select: boolean) => {
    if (!select) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredRows.map((r) => r._id)));
  };

  const rowsForScope = (scope: ContactsCsvScope): Array<JobContactRow> => {
    if (scope === "selected") {
      return filteredRows.filter((r) => selectedIds.has(r._id));
    }
    if (scope === "current") return filteredRows;
    return allRows;
  };

  const handleTableExport = (scope: ContactsCsvScope) => {
    const rowsToExport = rowsForScope(scope);
    if (rowsToExport.length === 0) {
      toast.error("No contacts to export.");
      return;
    }
    try {
      exportContactsTableToCsv(rowsToExport, scope);
      toast.success(
        `Exported ${rowsToExport.length} contact${rowsToExport.length === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to export contacts.",
      );
    }
  };

  const handleIpsExport = (scope: ContactsCsvScope) => {
    const rowsToExport = rowsForScope(scope);
    if (rowsToExport.length === 0) {
      toast.error("No contacts to export.");
      return;
    }
    try {
      exportContactsIpsToCsv(rowsToExport, sourceRowsByContact ?? null, scope);
      toast.success(
        `Exported ${rowsToExport.length} contact${rowsToExport.length === 1 ? "" : "s"} in IPS format.`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to export contacts.",
      );
    }
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkDeleteContacts({ contactIds: ids })
      .then((res) => {
        setBulkDeleteOpen(false);
        setSelectedIds(new Set());
        if (selectedContactId && selectedIds.has(selectedContactId)) {
          setSelectedContactId(null);
        }
        toast.success(
          `Deleted ${res.deleted} contact${res.deleted === 1 ? "" : "s"}.`,
        );
      })
      .catch((e: Error) => {
        toast.error(e.message || "Could not delete contacts.");
      });
  };

  const handleReconcileNow = () => {
    if (isReconciling) return;
    triggerReconcile({ jobId, force: true })
      .then((res) => {
        if (res.skipped) {
          toast.success("Already up to date.");
        } else if (res.summary) {
          const {
            jobContactsCreated,
            jobContactsTouched,
            jobContactsArchived,
          } = res.summary;
          toast.success(
            `Reconciled — ${jobContactsCreated} new, ${jobContactsTouched} updated, ${jobContactsArchived} archived.`,
          );
        } else {
          toast.success("Contacts refreshed.");
        }
      })
      .catch((e: Error) => {
        toast.error(e.message || "Could not reconcile contacts.");
      });
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
            <p className="text-sm text-muted-foreground">
              Contacts from your job are auto-populated here from searches and
              agent findings.
            </p>
            <p className="text-xs text-muted-foreground/80">
              Edit contact details, add notes, merge duplicated records and link
              job-level contacts to firm-level contact records.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canEdit && job && (
              <NewJobContactDialog jobId={jobId} orgId={job.orgId} />
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleReconcileNow}
              disabled={isReconciling}
              className="gap-2"
            >
              {isReconciling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {isReconciling ? "Refreshing…" : "Refresh Contacts"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={allRows.length === 0}
                  className="gap-2"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  Table (.csv)
                </DropdownMenuLabel>
                {selectedIds.size > 0 && (
                  <DropdownMenuItem
                    onClick={() => handleTableExport("selected")}
                  >
                    {selectedIds.size} selected
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleTableExport("current")}
                  disabled={filteredRows.length === 0}
                >
                  Current view
                  <span className="ml-auto text-xs text-muted-foreground">
                    {filteredRows.length}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleTableExport("all")}
                  disabled={allRows.length === 0}
                >
                  All contacts
                  <span className="ml-auto text-xs text-muted-foreground">
                    {allRows.length}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  IPS import format
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => handleIpsExport("current")}
                  disabled={filteredRows.length === 0}
                >
                  Current view
                  <span className="ml-auto text-xs text-muted-foreground">
                    {filteredRows.length}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleIpsExport("all")}
                  disabled={allRows.length === 0}
                >
                  All contacts
                  <span className="ml-auto text-xs text-muted-foreground">
                    {allRows.length}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setCustomExportOpen(true)}
                  disabled={allRows.length === 0}
                >
                  Custom export…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <CustomExportDialog
              open={customExportOpen}
              onOpenChange={setCustomExportOpen}
              datasets={customExportDatasets}
              title="Contacts"
            />
          </div>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, ABN, ACN, email, or phone…"
              className="h-9 pl-9 pr-9 text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-primary/40 hover:bg-secondary/60 hover:text-primary/70"
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          <FacetedFilter
            title="Role"
            options={roleFilterOptions}
            selectedValues={selectedRoleValues}
            onSelectionChange={(values) => {
              const nextBuiltIn = new Set<ContactRole>();
              const nextCustom = new Set<Id<"jobContactRoles">>();
              for (const value of values) {
                const customId = customIdByKey.get(value);
                if (customId) {
                  nextCustom.add(customId);
                } else if (isContactRole(value)) {
                  nextBuiltIn.add(value);
                }
              }
              setActiveRoles(nextBuiltIn);
              setActiveCustomRoleIds(nextCustom);
            }}
            facetCounts={roleCountsMap}
          />
          <FacetedFilter
            title="Type"
            options={TYPE_FILTER_OPTIONS}
            selectedValues={Array.from(activeTypes)}
            onSelectionChange={(v) =>
              setActiveTypes(new Set(v as ContactType[]))
            }
            facetCounts={typeCountsMap}
          />
          <Select
            value={groupView}
            onValueChange={(v) => setGroupView(v as ContactsGroupView)}
          >
            <SelectTrigger className="h-9 w-[190px] text-sm">
              <SelectValue placeholder="Groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="expanded">Grouped — all entities</SelectItem>
              <SelectItem value="collapsed">Grouped — top level</SelectItem>
              <SelectItem value="flat">Flat list</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBulkDeleteOpen(true)}
              className="h-7 gap-1.5 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete {selectedIds.size} selected
            </Button>
          )}
          {lowConfidenceCount > 0 && (
            <Badge color="red" variant="soft" size="sm" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              {lowConfidenceCount} low-confidence merge
              {lowConfidenceCount === 1 ? "" : "s"}
            </Badge>
          )}
          {(archivedCount > 0 || showArchived) && (
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {showArchived
                ? "Hide archived"
                : `Show ${archivedCount} archived`}
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {isReconciling && (
              <span
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                Refreshing contacts…
              </span>
            )}
            <span className="text-xs text-primary/50">
              {filteredRows.length === allRows.length
                ? `${allRows.length} contact${allRows.length === 1 ? "" : "s"}`
                : `${filteredRows.length} of ${allRows.length}`}
            </span>
            {isFiltered && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="h-7 px-2 text-xs text-primary/60"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table or empty / loading state */}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          Loading contacts…
        </div>
      ) : allRows.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 text-sm text-muted-foreground">
          <p>No contacts found yet.</p>
          <p className="text-xs">
            Contacts are seeded automatically from the agent findings — run an
            agent or upload a document
            {canEdit ? ', or use "Add Contact" to add one manually.' : "."}
          </p>
        </div>
      ) : (
        <ContactsDataTable
          rows={filteredRows}
          selectedContactId={selectedContactId}
          onSelectContact={(c) => setSelectedContactId(c._id)}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onToggleAll={toggleAllFiltered}
          groupView={groupView}
          customRolesById={customRolesById}
        />
      )}

      <ContactDetailPanel
        jobId={jobId}
        contactId={selectedContactId}
        open={selectedContactId !== null}
        onClose={() => setSelectedContactId(null)}
        jobContacts={allRows}
      />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} contact
              {selectedIds.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The selected contact{selectedIds.size === 1 ? "" : "s"} will be
              removed from this job, along with their notes and history. They
              will not be re-created by future contact refreshes. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBulkDelete();
              }}
              disabled={isBulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBulkDeleting ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : null}
              {isBulkDeleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
